'use client';
import React, { FC, ReactNode, useEffect, useRef } from 'react';
import Image from 'next/image';
import { useRouter } from '@/navigation';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  DynamicComponent,
  Errors,
  HeaderText,
  BreadcrumbTree,
} from '@/components';
import { TypeAccountPage } from '@/core/generated-types';
import { HeaderSize } from '@/components/ui/HeaderText';
import {
  ErrorDirectiveProvider,
  useErrorDirectives,
} from '@/context/ErrorDirectiveContext';
import { ERROR_KINDS } from '@/core/constants';
import { findApplicableErrorDetails } from '@/components/common/Errors/utils';
import { TypeAccountSettingsData } from '@/pages/[locale]/account/settings';
import { TypeMyAccountData } from '@/pages/[locale]/account';
import { PageDataProvider } from '@/context/PageDataProvider';
import AdminPreviewNotification from '@/components/layouts/account/AdminPreviewNotification';
import { TypeAddAChildPageData } from '@/pages/[locale]/account/settings/add-a-child';
import { TypeEditChildPageData } from '@/pages/[locale]/account/settings/edit-child/[slug]';
import { ConditionalBanner } from '@/components/common/ConditionalBanner';
import { setLoveveryIdInLocalStorage } from '@/lib/loveveryIdManager';
import ReferralBanners from '@/components/common/ConditionalBanner/components/ReferralBanners';
import { getAuthTokenClient } from '@/utils/helpers';
import useAuth from '@/context/AuthContext';
import { TypeUserAuthRecord } from '@/lib/api/accountManager/types';
import loader from '@/components/subflow-v3/assets/loader.gif';
import { TypeEditCommunicationPreferencesPageData } from '@/pages/[locale]/account/settings/edit-communication-preferences';
import { identifyCurrentUser } from '@/utils/rudderstack';
import playgeniusLogo from '@/public/assets/playgenius-logo.png';

export type AccountPageLayoutProps = {
  page: TypeAccountPage;
  // introduce union type for new page data options as they arise
  data?:
    | TypeAccountSettingsData
    | TypeAddAChildPageData
    | TypeEditChildPageData
    | TypeMyAccountData
    | TypeEditCommunicationPreferencesPageData;
  error?: string[];
  adminPreviewUserName?: string | undefined;
  children?: ReactNode;
  skillSetAccess?: Record<string, string>;
  isMyAccountPage?: boolean;
};

export const PageComponents: FC<AccountPageLayoutProps> = ({ page, error }) => {
  const {
    fields: { components },
  } = page;

  const { errorDirectives } = useErrorDirectives();
  const { errors, heading } = findApplicableErrorDetails(
    errorDirectives,
    ERROR_KINDS.DEFAULT,
  );

  if (error?.length) {
    error.map((err) => console.error(err));
    return <Errors errors={errors} heading={heading} />;
  }

  return (
    <section>
      {components &&
        components.map((component) => (
          <DynamicComponent key={component.sys.id} component={component} />
        ))}
    </section>
  );
};

const AccountPageLayout: FC<AccountPageLayoutProps> = ({
  page,
  data,
  error,
  adminPreviewUserName,
  children,
  skillSetAccess,
  isMyAccountPage,
}) => {
  const { user } = useAuth();
  const { push } = useRouter();

  const {
    fields: { title, breadcrumbTree, banner, referralBanners },
  } = page;
  const {
    'enable-bfcm': enableBfcm,
    'retention-enable-powered-by-pg-logo': isPgLogoEnabled,
  } = useFlags();

  const shouldShowPlaygeniusLogo = isMyAccountPage && isPgLogoEnabled;
  const loadCount = useRef(0);

  // Idenitfy user in rudderstack if the user is logged in
  useEffect(() => {
    if (user && user.attributes) {
      // keeping this from accountIdentifyEvent implementation
      setLoveveryIdInLocalStorage(user.attributes.loveveryId);
      identifyCurrentUser(user);
    }
  }, [user, user?.attributes]);

  // Send the userSignUp to Sailthru when the user is logged in
  useEffect(() => {
    const interval = setInterval(() => {
      loadCount.current += 1;
      if (loadCount.current > 10) clearInterval(interval);

      if (!window?.Sailthru) {
        // Wait for window.Sailthru to be available
        return;
      }

      if (data?.user) {
        const { email, name, family_name }: TypeUserAuthRecord =
          data.user as TypeUserAuthRecord;

        window.Sailthru.integration('userSignUp', {
          email,
          vars: {
            first_name: name,
            last_name: family_name,
          },
        });

        clearInterval(interval);
      }
    }, 500);
  }, [data?.user]);

  useEffect(() => {
    const triggerAccess = async () => {
      if (!skillSetAccess) return;

      try {
        await fetch('/api/v1/activate/wholesale_product_activation', {
          method: 'POST',
          body: JSON.stringify({
            anchor: skillSetAccess.anchor,
            user_uuid: user?.attributes.loveveryId,
            wholesale_source: 'amazon',
          }),
          headers: {
            'Content-Type': 'application/json',
            Authorization: await getAuthTokenClient(),
          },
        });
        push('/account/reading-skill-set');
      } catch (error) {
        console.log('Error activating wholesale product', error);
      }
    };

    triggerAccess();
  }, [push, skillSetAccess, user]);

  // Gives priority to the BFCM banner over advocate rewards when both exist
  const showReferralBanner =
    (data as TypeMyAccountData)?.hasAdvocateRewards && !enableBfcm;

  if (skillSetAccess) {
    return (
      <div className="mt-20 flex justify-center">
        <Image src={loader} alt="loader" />
      </div>
    );
  }

  return (
    <div className="mx-auto min-h-screen px-6 md:container lg:max-w-[1127px]">
      {banner && (
        <ConditionalBanner {...banner.fields} userUuid={data?.user.uuid} />
      )}
      {referralBanners && showReferralBanner && data?.user && (
        <ReferralBanners banners={referralBanners} user={data.user} />
      )}
      <AdminPreviewNotification
        adminName={adminPreviewUserName}
        userEmail={data?.user.email}
      />
      {breadcrumbTree && <BreadcrumbTree {...breadcrumbTree.fields} />}
      {shouldShowPlaygeniusLogo && (
        <div className="flex items-center justify-center mt-10">
          <Image src={playgeniusLogo} alt="powered-by-playgenius" width={130} />
        </div>
      )}
      <HeaderText
        text={title}
        align="left"
        size={HeaderSize.H1}
        classes="lg:my-10 my-8"
      />
      <PageDataProvider data={data}>
        <ErrorDirectiveProvider errorDirectives={page.fields.errorDirectives}>
          <PageComponents page={page} error={error} />
          {children}
        </ErrorDirectiveProvider>
      </PageDataProvider>
    </div>
  );
};

export default React.memo(AccountPageLayout);
